import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query';
import {
  GetAllCharacterListResponse,
  GetPersonaDetailsResponse,
  Chat01RoomType,
  PostCharacterLikeRequest,
} from '~/types';
import userKey from '../user/userKey';
import characterKey from './characterKey';

export type UpdateCharacterLikeRequest = {
  type?: Chat01RoomType;
  query?: string;
} & PostCharacterLikeRequest;
export type UpdateCharacterLikeMutationType = 'like' | 'unlike';

const useUpdateCharacterLike = () => {
  const queryClient = useQueryClient();

  const updateCharacterData = <T extends { result: number; data: any[] }>(
    queryKey: QueryKey,
    operation: UpdateCharacterLikeMutationType,
    character_id: string,
  ) => {
    const prev = queryClient.getQueryData<T>(queryKey);
    if (!prev || !prev.data) return;

    queryClient.setQueryData(queryKey, {
      ...prev,
      data: prev.data.map((el) => {
        return el.character_id !== character_id
          ? el
          : {
              ...el,
              is_liked: !el.is_liked,
              liked_count: el.liked_count + (operation === 'like' ? 1 : -1),
            };
      }),
    });
  };

  const updateInfiniteCharacterData = <
    T extends { result: number; data: any[] },
  >(
    queryKey: QueryKey,
    operation: UpdateCharacterLikeMutationType,
    character_id: string,
  ) => {
    const prev = queryClient.getQueryData<InfiniteData<T>>(queryKey);
    if (!prev) return;

    queryClient.setQueryData(queryKey, {
      ...prev,
      pages: prev.pages.map((page) => {
        return !page.data
          ? page
          : {
              ...page,
              data: page.data.map((el) => {
                return el.character_id !== character_id
                  ? el
                  : {
                      ...el,
                      is_liked: !el.is_liked,
                      liked_count:
                        el.liked_count + (operation === 'like' ? 1 : -1),
                    };
              }),
            };
      }),
    });
  };

  const updateExploreCharacter = (
    { character_id }: UpdateCharacterLikeRequest,
    operation: UpdateCharacterLikeMutationType,
  ) =>
    updateCharacterData(
      characterKey.getExploreCharacterList(),
      operation,
      character_id,
    );

  const updateCharacterListByType = (
    { character_id, type = 'user' }: UpdateCharacterLikeRequest,
    operation: UpdateCharacterLikeMutationType,
  ) =>
    updateCharacterData(
      characterKey.getListByType({ type }),
      operation,
      character_id,
    );

  const updateCelebList = (
    { character_id }: UpdateCharacterLikeRequest,
    operation: UpdateCharacterLikeMutationType,
  ) =>
    updateCharacterData(characterKey.getCelebList(), operation, character_id);

  const updateSearchCharacter = (
    { character_id, query }: UpdateCharacterLikeRequest,
    operation: UpdateCharacterLikeMutationType,
  ) =>
    updateInfiniteCharacterData(
      characterKey.getSearchCharacterList({ query }),
      operation,
      character_id,
    );

  const updatePopularCharacter = (
    { character_id }: UpdateCharacterLikeRequest,
    operation: UpdateCharacterLikeMutationType,
  ) =>
    updateInfiniteCharacterData(
      characterKey.getPopularCharacterList(),
      operation,
      character_id,
    );

  const updatePersonaDetails = (
    { character_id }: UpdateCharacterLikeRequest,
    operation: UpdateCharacterLikeMutationType,
  ) => {
    const prev = queryClient.getQueryData<GetPersonaDetailsResponse>(
      characterKey.getPersonaDetails({ character_id }),
    );
    if (!prev || !prev.data) return;

    queryClient.setQueryData(characterKey.getPersonaDetails({ character_id }), {
      ...prev,
      data: {
        ...prev.data,
        is_liked: !prev.data.is_liked,
        liked_count: prev.data.liked_count + (operation === 'like' ? 1 : -1),
      },
    });
  };

  const updateMyFavoriteCharacter = (
    { character_id }: UpdateCharacterLikeRequest,
    operation: UpdateCharacterLikeMutationType,
  ) => {
    if (operation === 'like') {
      queryClient.invalidateQueries({
        queryKey: userKey.getMyFavorite(),
      });
      return;
    }
    const prev = queryClient.getQueryData<GetAllCharacterListResponse>(
      userKey.getMyFavorite(),
    );
    if (!prev || !prev.data) return;
    queryClient.setQueryData(userKey.getMyFavorite(), {
      ...prev,
      data: prev.data.filter((el) => el.character_id !== character_id),
    });
  };

  const handleUpdateCharacterLike = (
    operation: UpdateCharacterLikeMutationType,
    request: UpdateCharacterLikeRequest,
  ) => {
    updateExploreCharacter(request, operation);
    updateCharacterListByType(request, operation);
    updateCelebList(request, operation);
    updateSearchCharacter(request, operation);
    updatePopularCharacter(request, operation);
    updatePersonaDetails(request, operation);
    updateMyFavoriteCharacter(request, operation);
  };

  return {
    handleUpdateCharacterLike,
  };
};

export default useUpdateCharacterLike;
